import {Order} from "./Order";
import {OrderCollection} from "./OrderCollection";
import {OrderEntry} from "./OrderEntry/OrderEntry";
import {OrderTypeEnum} from "./OrderTypeEnum";
import {OrderEntryStatusEnum} from "./OrderEntry/OrderEntryStatusEnum";

import moment from "moment";
import {CompanyCollection} from "../Company/CompanyCollection";
import {Company} from "../Company/Company";
import {ProductPricingCollection} from "../ProductPricing/ProductPricingCollection";
import * as firebase from 'firebase'

export interface IWeekOrders {
  restaurantId: string
  restaurantName: string
  restaurantIdentifier: string
  weekOrdered: Date
  ordersCount: number
  ordersTotalPrice: number
  ordersAveragePrice: number
}

export class OrderService {
  private orderCollection: OrderCollection

  constructor(protected db: firebase.firestore.Firestore) {
    this.orderCollection = new OrderCollection(db);
  }

  public async addEntryToBackorder(order: Order, entry: OrderEntry, companyId: string) {
    if (
      (entry.receivedQuantity || 0) >= entry.quantity
      && entry.status !== OrderEntryStatusEnum.REJECTED
    ) {
      return Promise.resolve()
    }

    const backorder = await this.getBackorder(order, companyId);
    const backorderEntry = new OrderEntry(entry.upc, entry.description);

    backorderEntry.vendorCompanyId = entry.vendorCompanyId;
    backorderEntry.priceListEntryId = entry.priceListEntryId;
    backorderEntry.priceListId = entry.priceListId;
    backorderEntry.pricePerUnit = entry.pricePerUnit;
    backorderEntry.image = entry.image;
    backorderEntry.orderingNotes = entry.orderingNotes;
    backorderEntry.category = entry.category;
    backorderEntry.originalEntryId = entry.id;
    backorderEntry.quantity = entry.quantity - (entry.receivedQuantity || 0);
    backorderEntry.quantityType = entry.quantityType;

    backorder.orderEntries.push(backorderEntry);

    return this.orderCollection.save(backorder);
  }

  protected async getBackorder(order: Order, companyId: string): Promise<Order> {
    const existingBackorder = await this.orderCollection.getBackorderByRestaurantOrderId(order.id, companyId);

    if (existingBackorder) {
      return existingBackorder;
    }

    const newBackorder = new Order(
      `${order.number}-backorder`,
      OrderTypeEnum.RESTAURANT,
      order.orderedByUserId,
      order.orderedByCompanyId,
      order.orderedByCompanyName,
      order.orderedByCompanyId
    );
    newBackorder.originalOrderId = order.id;

    return this.orderCollection.save(newBackorder);
  }

  public async getTotalOrderVolume(weekOrders: IWeekOrders[]) {
    const totalWeekOrders = weekOrders.reduce((acc, weekOrder) => {
      const alreadyExists = acc.find(
        we => moment(we.weekOrdered).isSame(weekOrder.weekOrdered)
      );

      if (alreadyExists) {
        alreadyExists.ordersCount += weekOrder.ordersCount;
        alreadyExists.ordersTotalPrice += weekOrder.ordersTotalPrice;
        alreadyExists.ordersAveragePrice = (alreadyExists.ordersTotalPrice / alreadyExists.ordersCount) || 0;
        return acc;
      } else {
        const totalWeekOrder = {
          restaurantId: '1',
          restaurantName: 'Total Across All Locations',
          restaurantIdentifier: '',
          weekOrdered: weekOrder.weekOrdered,
          ordersCount: weekOrder.ordersCount,
          ordersTotalPrice: weekOrder.ordersTotalPrice,
          ordersAveragePrice: weekOrder.ordersAveragePrice
        } as IWeekOrders;
        return [...acc, totalWeekOrder];
      }
    }, [] as IWeekOrders[]);

    return totalWeekOrders;
  }

  public async getOrderVolume(startDate: Date, endDate: Date) {
    const endOfEndWeek = moment(endDate).add('week', 1).startOf('week')
    const startOfStartWeek = moment(startDate).startOf('week')

    const duration = moment.duration(endOfEndWeek.diff(startOfStartWeek)).asWeeks()

    let date = moment(startDate)
    const weeks:[number, moment.Moment][] = []
    for (let i = 0; i < duration; i++) {
      const week = date.week();
      weeks.push([week, moment(date).startOf('week').week(week)])
      date = date.add('week', 1)
    }

    const orders = await this.orderCollection.listdOrdersByTypeAndDatePlacedRange(
      OrderTypeEnum.VENDOR,
      startOfStartWeek.toISOString(),
      endOfEndWeek.toISOString()
    );

    const companyIds = orders.reduce((ids, order) => {
      return !ids.includes(order.orderedByCompanyId)
        ? [...ids, order.orderedByCompanyId]
        : ids;
    }, [] as string[]);

    const companyCollection = new CompanyCollection(this.db);

    const companies = (await Promise.all(companyIds.map(id =>
      companyCollection.getCompany(id)
    ))).filter(company => !!company) as Company[];

    const weekOrdersMap = companies.reduce((wom, company) => {
      weeks.forEach(week => {
        wom[`${company.id}_${week[0]}_${week[1].startOf('week').year()}`] = {
          restaurantId: company.id,
          restaurantName: company.name,
          restaurantIdentifier: company.identifier || '',
          weekOrdered: week[1].toDate(),
          ordersCount: 0,
          ordersTotalPrice: 0,
          ordersAveragePrice: 0
        }
      });

      return wom;
    }, {} as Record<string, IWeekOrders>);

    const restaurantWeekOrdersMap = orders.reduce((rwom, order) => {
      if (order.datePlaced) {
        const datePlaced = moment(order.datePlaced, moment.ISO_8601)
        const key = `${order.orderedByCompanyId}_${datePlaced.week()}_${datePlaced.startOf('week').year()}`;
        rwom[key] = [...(rwom[key] || []), order];
      }

      return rwom;
    }, {} as Record<string, Order[]>);

    const pricingCollection = new ProductPricingCollection(this.db);
    const pricingCache = {} as Record<string, number>;

    await Promise.all(
      Object.entries(restaurantWeekOrdersMap).map(
        async ([key, restaurantWeekOrders]) => {
          const weekMetrics = await this.getWeekMetrics(
            restaurantWeekOrders,
            pricingCollection,
            pricingCache
          );

          weekOrdersMap[key] = {
            ...weekOrdersMap[key],
            ...weekMetrics
          };
        }
      )
    );

    return Object.values(weekOrdersMap);
  }

  private async getWeekMetrics(
    orders: Order[],
    pricingCollection: ProductPricingCollection,
    pricingCache: Record<string, number>,
  ) {
    const ordersTotal = await Promise.all(
      orders.map(order => this.getOrderTotal(order, pricingCollection, pricingCache))
    );

    const ordersTotalPrice = ordersTotal.reduce((t, p) => t + p, 0);

    return {
      ordersCount: orders.length,
      ordersTotalPrice,
      ordersAveragePrice: ordersTotalPrice / orders.length
    };
  }

  private async getOrderTotal(
    order: Order,
    pricingCollection: ProductPricingCollection,
    pricingCache: Record<string, number>
  ) {
    const entryPrices = await Promise.all(
      order.orderEntries.map(
        async ({quantity, description, pricePerUnit}) => {
          if (pricePerUnit) {
            return pricePerUnit * quantity;
          }

          const cachedPrice = pricingCache[description];
          if (cachedPrice) {
            return cachedPrice * quantity
          }

          const productPricing = await pricingCollection
            .getProductPricingByDescription(description);

          if (productPricing) {
            pricingCache[description] = productPricing.price;
            return productPricing.price * quantity;
          }

          return 0;
        }
      )
    );

    return entryPrices.reduce((t, p) => t + p, 0);
  }
}
